// WebSocket transport. Owns the connection lifecycle: connect, hello handshake,
// heartbeat/ping round-trips, auto-reconnect with capped exponential backoff,
// and typed dispatch of decoded server messages to registered callbacks.

import {
  encode,
  decodeServerMessage,
  PROTOCOL_VERSION,
  PING_INTERVAL_MS,
  HEARTBEAT_TIMEOUT_MS,
} from "@mmo/shared";
import type { ClientMessage, ServerMessage, EntityState, MoneyState } from "@mmo/shared";

export type ConnStatus = "connecting" | "connected" | "disconnected";

/** Narrowed payloads handed to callbacks. */
type Welcome = Extract<ServerMessage, { t: "welcome" }>;
export type PathEvent = Extract<ServerMessage, { t: "path" }>;
export type FireballEvent = Extract<ServerMessage, { t: "fireball" }>;

export interface NetCallbacks {
  onStatusChange?: (status: ConnStatus) => void;
  onWelcome?: (msg: Welcome) => void;
  /** One or more entities became visible (entered the AOI / initial dump). */
  onEnter?: (entities: EntityState[]) => void;
  /** A known entity changed its waypoint. */
  onPath?: (ev: PathEvent) => void;
  onLeave?: (id: number) => void;
  /** An entity's HP changed. */
  onHp?: (id: number, hp: number) => void;
  /** An entity's display name changed. */
  onRename?: (id: number, name: string) => void;
  /** A fireball was launched (visual only). */
  onFireball?: (ev: FireballEvent) => void;
  /** An entity was authoritatively repositioned (respawn). */
  onTeleport?: (id: number, x: number, y: number) => void;
  onMoneySpawn?: (money: MoneyState) => void;
  onMoneyGone?: (id: number) => void;
  onGold?: (amount: number) => void;
  /** round-trip time in ms for the most recent ping. */
  onPong?: (rttMs: number) => void;
  onEmailSent?: (purpose: "bind" | "signin") => void;
  onEmailBound?: (email: string) => void;
  onSignedIn?: (token: string) => void;
  onError?: (code: string, message: string) => void;
}

// Reconnect backoff schedule (ms), capped at the last entry.
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000, 15000];

export class Net {
  private readonly url: string;
  private readonly cb: NetCallbacks;

  private ws: WebSocket | null = null;
  private status: ConnStatus = "connecting";
  private closedByUser = false;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;

  // Heartbeat bookkeeping.
  private pingTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private pingId = 0;
  private pingSentAt = new Map<number, number>();

  // Identity sent with every (re)connect's hello.
  private token: string | null;
  private name: string | null;

  constructor(token: string | null, name: string | null, cb: NetCallbacks) {
    this.token = token;
    this.name = name;
    this.cb = cb;

    const base =
      import.meta.env.VITE_SERVER_URL || "ws://" + location.hostname + ":8080";
    this.url = base.replace(/\/+$/, "") + "/ws";
  }

  /** Update the identity used for the next hello (e.g. after a name change). */
  setIdentity(token: string | null, name: string | null): void {
    this.token = token;
    this.name = name;
  }

  connect(): void {
    this.closedByUser = false;
    this.openSocket();
  }

  /** Permanently close (no reconnect). */
  dispose(): void {
    this.closedByUser = true;
    this.clearTimers();
    this.ws?.close();
    this.ws = null;
  }

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(encode(msg));
    }
  }

  getStatus(): ConnStatus {
    return this.status;
  }

  // -------------------------------------------------------------------------
  private setStatus(s: ConnStatus): void {
    if (s === this.status) return;
    this.status = s;
    this.cb.onStatusChange?.(s);
  }

  private openSocket(): void {
    this.setStatus(this.reconnectAttempt === 0 ? "connecting" : "connecting");

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      // Note: backoff is reset only after a real handshake (welcome), not here.
      // An open-then-close cycle (e.g. server replies with `error` and closes)
      // must keep escalating the backoff instead of resetting to 500ms.
      this.setStatus("connected");
      this.send({
        t: "hello",
        protocol: PROTOCOL_VERSION,
        token: this.token ?? undefined,
        name: this.name ?? undefined,
      });
      this.startHeartbeat();
    };

    ws.onmessage = (ev) => {
      // Any inbound traffic proves the link is alive.
      this.bumpHeartbeat();
      if (typeof ev.data !== "string") return;
      const msg = decodeServerMessage(ev.data);
      if (msg) this.dispatch(msg);
    };

    ws.onerror = () => {
      // The browser fires `close` right after; reconnect is handled there.
    };

    ws.onclose = () => {
      this.clearTimers();
      this.ws = null;
      if (this.closedByUser) return;
      this.setStatus("disconnected");
      this.scheduleReconnect();
    };
  }

  private dispatch(msg: ServerMessage): void {
    switch (msg.t) {
      case "welcome":
        // A completed handshake is the only proof the connection is healthy,
        // so reset the reconnect backoff here rather than on raw socket open.
        this.reconnectAttempt = 0;
        this.cb.onWelcome?.(msg);
        break;
      case "enter":
        this.cb.onEnter?.(msg.entities);
        break;
      case "path":
        this.cb.onPath?.(msg);
        break;
      case "hp":
        this.cb.onHp?.(msg.id, msg.hp);
        break;
      case "rename":
        this.cb.onRename?.(msg.id, msg.name);
        break;
      case "fireball":
        this.cb.onFireball?.(msg);
        break;
      case "teleport":
        this.cb.onTeleport?.(msg.id, msg.x, msg.y);
        break;
      case "money_spawn":
        this.cb.onMoneySpawn?.(msg.money);
        break;
      case "money_gone":
        this.cb.onMoneyGone?.(msg.id);
        break;
      case "gold":
        this.cb.onGold?.(msg.amount);
        break;
      case "leave":
        this.cb.onLeave?.(msg.id);
        break;
      case "pong": {
        const sentAt = this.pingSentAt.get(msg.id);
        if (sentAt !== undefined) {
          this.pingSentAt.delete(msg.id);
          this.cb.onPong?.(Math.round(performance.now() - sentAt));
        }
        break;
      }
      case "email_sent":
        this.cb.onEmailSent?.(msg.purpose);
        break;
      case "email_bound":
        this.cb.onEmailBound?.(msg.email);
        break;
      case "signed_in":
        this.cb.onSignedIn?.(msg.token);
        break;
      case "error":
        this.cb.onError?.(msg.code, msg.message);
        break;
      default: {
        // Exhaustiveness guard: a new server message type would surface here.
        const _never: never = msg;
        void _never;
      }
    }
  }

  // -- Heartbeat ------------------------------------------------------------
  private startHeartbeat(): void {
    this.clearHeartbeatTimers();
    this.bumpHeartbeat();
    this.pingTimer = window.setInterval(() => this.sendPing(), PING_INTERVAL_MS);
    // Send one immediately so the ping HUD populates quickly.
    this.sendPing();
  }

  private sendPing(): void {
    const id = ++this.pingId;
    this.pingSentAt.set(id, performance.now());
    // Bound the outstanding map in case pongs are lost.
    if (this.pingSentAt.size > 16) {
      const oldest = this.pingSentAt.keys().next().value;
      if (oldest !== undefined) this.pingSentAt.delete(oldest);
    }
    this.send({ t: "ping", id });
  }

  /** Reset the dead-connection timeout whenever the server is heard from. */
  private bumpHeartbeat(): void {
    if (this.heartbeatTimer !== null) window.clearTimeout(this.heartbeatTimer);
    this.heartbeatTimer = window.setTimeout(() => {
      // Silent for too long — assume the link is dead and force a reconnect.
      this.ws?.close();
    }, HEARTBEAT_TIMEOUT_MS);
  }

  private clearHeartbeatTimers(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.heartbeatTimer !== null) {
      window.clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.pingSentAt.clear();
  }

  // -- Reconnect ------------------------------------------------------------
  private scheduleReconnect(): void {
    if (this.closedByUser || this.reconnectTimer !== null) return;
    const delay =
      BACKOFF_MS[Math.min(this.reconnectAttempt, BACKOFF_MS.length - 1)];
    this.reconnectAttempt++;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private clearTimers(): void {
    this.clearHeartbeatTimers();
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
